
import { Module } from '../types';

export const CONTEXT_STATE_MODULE: Module = {
  id: "intermediate-context",
  title: "Context API & Global State Management",
  description: "Learn to manage global application state with React Context API and avoid prop drilling.",
  duration: "1.5 hours",
  estimatedTime: 90,
  difficulty: "intermediate",
  prerequisites: ["intermediate-hooks"],
  content: `
# Context API & Global State Management

## Learning Objectives
- Understand when to use Context API vs local state
- Create and consume React contexts effectively
- Implement global state management patterns
- Avoid common Context pitfalls and performance issues
- Build scalable state architecture

## Understanding React Context

### The Problem: Prop Drilling
\`\`\`jsx
// Without Context - Prop drilling nightmare
function App() {
  const [user, setUser] = useState(null);
  
  return <Layout user={user} setUser={setUser} />;
}

function Layout({ user, setUser }) {
  return (
    <div>
      <Header user={user} setUser={setUser} />
      <Main user={user} />
    </div>
  );
}

function Header({ user, setUser }) {
  return <UserMenu user={user} setUser={setUser} />;
}

function UserMenu({ user, setUser }) {
  // Finally using the props!
  return user ? <div>Welcome {user.name}</div> : <LoginButton onClick={setUser} />;
}
\`\`\`

### The Solution: Context API
\`\`\`jsx
import React, { createContext, useContext, useState } from 'react';

// Create context
const UserContext = createContext();

// Provider component
export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  
  const login = (userData) => setUser(userData);
  const logout = () => setUser(null);
  
  return (
    <UserContext.Provider value={{ user, login, logout }}>
      {children}
    </UserContext.Provider>
  );
}

// Custom hook for using context
export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
}

// Usage - No prop drilling!
function UserMenu() {
  const { user, logout } = useUser();
  
  return user ? (
    <div>
      Welcome {user.name}
      <button onClick={logout}>Logout</button>
    </div>
  ) : (
    <LoginButton />
  );
}
\`\`\`

## Advanced Context Patterns

### Multiple Contexts for Separation of Concerns
\`\`\`jsx
// Auth Context
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Check for existing session
    checkAuthStatus().then(user => {
      setUser(user);
      setLoading(false);
    });
  }, []);
  
  const login = async (credentials) => {
    const user = await authenticateUser(credentials);
    setUser(user);
    return user;
  };
  
  const logout = async () => {
    await logoutUser();
    setUser(null);
  };
  
  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

// Theme Context
const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState('light');
  
  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };
  
  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// App with multiple providers
function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <Routes>
            <Route path="/" element={<HomePage />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}
\`\`\`

### Context with useReducer for Complex State
\`\`\`jsx
// Shopping cart state management
const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM':
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        };
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload, quantity: 1 }]
      };
      
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };
      
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      };
      
    case 'CLEAR_CART':
      return { items: [] };
      
    default:
      return state;
  }
};

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });
  
  const addItem = (item) => dispatch({ type: 'ADD_ITEM', payload: item });
  const removeItem = (id) => dispatch({ type: 'REMOVE_ITEM', payload: id });
  const updateQuantity = (id, quantity) => 
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } });
  const clearCart = () => dispatch({ type: 'CLEAR_CART' });
  
  const totalItems = state.items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = state.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  return (
    <CartContext.Provider value={{
      items: state.items,
      totalItems,
      totalPrice,
      addItem,
      removeItem,
      updateQuantity,
      clearCart
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
\`\`\`

## Performance Optimization

### Splitting Context to Prevent Unnecessary Re-renders
\`\`\`jsx
// Problem: Single context causes all consumers to re-render
const AppContext = createContext();

// Solution: Split into separate contexts
const UserStateContext = createContext();
const UserActionsContext = createContext();

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  
  // Actions don't change, so they won't cause re-renders
  const actions = useMemo(() => ({
    login: (userData) => setUser(userData),
    logout: () => setUser(null),
    updateProfile: (updates) => setUser(prev => ({ ...prev, ...updates }))
  }), []);
  
  return (
    <UserStateContext.Provider value={user}>
      <UserActionsContext.Provider value={actions}>
        {children}
      </UserActionsContext.Provider>
    </UserStateContext.Provider>
  );
}

// Separate hooks for state and actions
export const useUserState = () => useContext(UserStateContext);
export const useUserActions = () => useContext(UserActionsContext);
\`\`\`

### Using React.memo with Context
\`\`\`jsx
// Expensive component that should only re-render when its props change
const ExpensiveUserProfile = React.memo(({ userId }) => {
  console.log('ExpensiveUserProfile rendering');
  
  // This component only re-renders when userId changes
  // not when other context values change
  return <div>User Profile for {userId}</div>;
});

function UserDashboard() {
  const { user } = useUser();
  const { theme } = useTheme(); // Won't cause ExpensiveUserProfile to re-render
  
  return (
    <div>
      <ExpensiveUserProfile userId={user?.id} />
      <div>Current theme: {theme}</div>
    </div>
  );
}
\`\`\`

## Best Practices

### 1. Create Custom Hooks for Context
\`\`\`jsx
// Good: Custom hook with error handling
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Usage
function ProtectedComponent() {
  const { user, loading } = useAuth(); // Clear and safe
  
  if (loading) return <Spinner />;
  if (!user) return <LoginForm />;
  
  return <div>Welcome {user.name}</div>;
}
\`\`\`

### 2. Provide Default Values
\`\`\`jsx
const defaultValue = {
  user: null,
  login: () => {},
  logout: () => {},
  loading: false
};

const AuthContext = createContext(defaultValue);
\`\`\`

### 3. Keep Context Values Stable
\`\`\`jsx
// Bad: Creates new object on every render
function BadProvider({ children }) {
  const [user, setUser] = useState(null);
  
  return (
    <AuthContext.Provider value={{ user, setUser }}> {/* New object each time */}
      {children}
    </AuthContext.Provider>
  );
}

// Good: Memoize the value
function GoodProvider({ children }) {
  const [user, setUser] = useState(null);
  
  const value = useMemo(() => ({ user, setUser }), [user]);
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
\`\`\`

## When NOT to Use Context

- For component-specific state
- For frequently changing values (like input values)
- When you need time-travel debugging (use Redux instead)
- For server state (use React Query/SWR instead)

## Real-World Example: Complete App State Management

\`\`\`jsx
// providers/AppProviders.jsx
import { AuthProvider } from './AuthProvider';
import { ThemeProvider } from './ThemeProvider';
import { CartProvider } from './CartProvider';
import { NotificationProvider } from './NotificationProvider';

export function AppProviders({ children }) {
  return (
    <AuthProvider>
      <ThemeProvider>
        <CartProvider>
          <NotificationProvider>
            {children}
          </NotificationProvider>
        </CartProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

// App.jsx
function App() {
  return (
    <AppProviders>
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Routes>
      </Router>
    </AppProviders>
  );
}
\`\`\`
`,
  videoUrl: "https://example.com/context-api",
  codeExamples: [
    {
      title: "Basic Context Setup",
      code: `const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState('light');
  
  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);`,
      explanation: "Basic pattern for creating and using context",
      language: "jsx"
    }
  ],
  practice: {
    task: "Create a TodoContext that manages a list of todos with add, toggle, and delete functionality. Include proper TypeScript types and error handling.",
    starter: `// Create your TodoContext here
const TodoContext = createContext();

export function TodoProvider({ children }) {
  // Implement todo state management
}

export function useTodos() {
  // Implement the custom hook
}

function TodoApp() {
  // Use the context in your app
  return (
    <TodoProvider>
      <TodoList />
      <AddTodoForm />
    </TodoProvider>
  );
}`,
    solution: `interface Todo {
  id: string;
  text: string;
  completed: boolean;
}

interface TodoContextType {
  todos: Todo[];
  addTodo: (text: string) => void;
  toggleTodo: (id: string) => void;
  deleteTodo: (id: string) => void;
}

const TodoContext = createContext<TodoContextType | undefined>(undefined);

export function TodoProvider({ children }: { children: React.ReactNode }) {
  const [todos, setTodos] = useState<Todo[]>([]);
  
  const addTodo = useCallback((text: string) => {
    setTodos(prev => [...prev, {
      id: Date.now().toString(),
      text,
      completed: false
    }]);
  }, []);
  
  const toggleTodo = useCallback((id: string) => {
    setTodos(prev => prev.map(todo =>
      todo.id === id ? { ...todo, completed: !todo.completed } : todo
    ));
  }, []);
  
  const deleteTodo = useCallback((id: string) => {
    setTodos(prev => prev.filter(todo => todo.id !== id));
  }, []);
  
  const value = useMemo(() => ({
    todos,
    addTodo,
    toggleTodo,
    deleteTodo
  }), [todos, addTodo, toggleTodo, deleteTodo]);
  
  return (
    <TodoContext.Provider value={value}>
      {children}
    </TodoContext.Provider>
  );
}

export function useTodos() {
  const context = useContext(TodoContext);
  if (context === undefined) {
    throw new Error('useTodos must be used within a TodoProvider');
  }
  return context;
}`,
    hints: [
      "Use TypeScript interfaces for type safety",
      "Memoize the context value to prevent unnecessary re-renders",
      "Use useCallback for action functions",
      "Include proper error handling in your custom hook"
    ],
    estimatedTime: 45
  },
  quickCheck: {
    questions: [
      {
        id: "context-q1",
        question: "What problem does React Context solve?",
        options: [
          "Component re-rendering",
          "Prop drilling",
          "Memory leaks",
          "Bundle size"
        ],
        correctAnswer: 1,
        correctAnswerIndex: 1,
        difficulty: "easy"
      },
      {
        id: "context-q2",
        question: "When should you split a Context into multiple contexts?",
        options: [
          "Never, one context is always better",
          "When you have unrelated state that updates at different frequencies",
          "Only when using TypeScript",
          "When you have more than 5 properties"
        ],
        correctAnswer: 1,
        correctAnswerIndex: 1,
        difficulty: "medium"
      },
      {
        id: "context-q3",
        question: "What should you do to prevent unnecessary re-renders with Context?",
        options: [
          "Use React.memo and memoize context values",
          "Never use Context",
          "Always use Redux instead",
          "Use only class components"
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0,
        difficulty: "hard"
      }
    ]
  }
};
